<template>
    <div class="content" :class="[$mq]">
        <div class="titles">
            <span>{{ $t('production.plk.pending-to-make.product') }}</span>
            <span>{{ $t('production.plk.pending-to-make.make') }}</span>
            <span>{{ $t('production.plk.pending-to-make.time') }}</span>
        </div>
        <div class="products">
            <div class="product" v-for="(product, hourKey) in currentProducts" :key="product.id" @click="addProduct(product)" :class="{'overdue': product.currentTime <= 0}">
                <span class="name">{{ product.name }}</span>
                <div class="units" :class="{'close-to-make': product.currentTime <= 5}">
                    <span>{{ product.currentUnits }}</span>
                    <span>{{ product.unitName }}</span>
                </div>
                <div class="time">
                    <span>{{ product.currentTime }}</span>
                    <span>min</span>
                </div>
                <i class="i-andy-chevron-right i-m" @click="addProduct(product)"></i>
                <span v-if="product.currentTime <= 0" class="recommended-date">{{ formatTime(product.orginalRecommendedDate) }}</span>
            </div>
        </div>
        <div class="debug" v-if="debug">
            <pre>
                <DebugPendingToMake 
                    :products="products" 
                    :currentProducts="currentProducts"
                    :currentReferenceTime="currentReferenceTime.format('YYYY-MM-DD HH:mm:ss')"
                    :productsCooking="productsCooking"
                />
            </pre>
        </div>
    </div>
</template>

<script>
import { formatDisplayDateFromStore } from '@/utils/dateFormatters'
import DebugPendingToMake from './debugPendingToMake.vue'
export default {
    name: 'PendingToMake',
    components: {
        DebugPendingToMake
    },
    props: {
        products: {
            type: Array,
            required: true,
            default: () => []
        },
        debug: {
            type: Boolean,
            default: false
        },
        productsCooking: {
            type: Array,
            default: () => []
        },
    },

    data() {
        return {
            currentReferenceTime: moment() // Hora de referencia que se puede avanzar
        }
    },

    computed: {
        currentProducts() {
            return this.products
                .filter(product => product.currentUnits > 0) // Solo filtrar por unidades, no por tiempo
                .sort((a, b) => a.currentTime - b.currentTime) // Ordenar por tiempo restante
        }
    },

    methods: {
        /**
         * Maneja el clic en un producto para agregarlo
         */
        addProduct(product) {
            // Emitir evento y esperar respuesta del padre
            this.$emit('addProduct', product, (success) => {
                // El tiempo ya está calculado en el componente padre
                // No necesitamos avanzar manualmente aquí
            })
        },

        formatTime(time) {
            //Valid time format with moment
            if(!moment(time).isValid()) {
                return time;
            }

            return formatDisplayDateFromStore(time, { includeTime: true })
        }
    }
}
</script>

<style lang="scss" scoped>
.content {
    $product-width: 1.5fr;
    background-color: $color-white;
    width: 100%;
    height: 100%;
    display: grid;
    gap: 8px;
    padding-right: 16px;
    grid-template-columns: $product-width 1fr 1fr;
    grid-template-areas:
        "titles titles titles"
        "products products products";
    grid-template-rows: auto 1fr;
    overflow: hidden;

    .debug {
        position: absolute;
        top: 0;
        right: 0;
        width: 500px;
        height: 100%;
        overflow: auto;
        background-color: $color-white;
        padding: 16px;
        z-index: 1000;
        background-color: $color-white;
        border-radius: 4px;
        border: 1px solid $color-neutral-200;
        @include font-size(12px);
        font-family: $text;
        color: $color-neutral-600;
    }

    .titles {
        padding: 16px;
        display: grid;
        grid-area: titles;
        grid-template-columns: $product-width 1fr 1fr;
        gap: 8px;

        span {
            text-align: center;
            font-family: $text;
            @include font-size(22px);
            text-transform: uppercase;
            color: $color-neutral-600;
            padding: 8px;
            background-color: $color-neutral-200;
            border-radius: 4px;
        }
    }

    .products {
        grid-area: products;
        padding: 16px;
        @include display-flex();
        @include flex-direction(column);
        gap: 8px;
        padding-right: 32px;

        // Responsive height - usar viewport height en lugar de altura fija
        min-height: 70vh;
        max-height: 70vh;
        overflow: auto;


        .product {
            display: grid;
            grid-template-columns: $product-width 1fr 1fr;
            gap: 8px;
            padding: 8px;
            border-radius: 4px;
            position: relative;
            cursor: pointer;

            .recommended-date {
                position: absolute;
                bottom: 30%;
                left: 8px;
                transform: translateY(50%);
                width: 100%;
                text-align: left;
                font-family: $text;
                @include font-size(12px);
                color: $color-white;
            }

            &.overdue {
                background: linear-gradient(45deg, #bd1f1e, #cc4141, #bd1f1e, #cc4141, #bd1f1e);
                background-size: 200% 200%;
                animation: gradient 5s linear infinite;


                .name {
                    color: $color-white;
                }

                .units {
                    background-color: $color-error-800 !important;
                    border-color: $color-error-800 !important;

                    >span {
                        color: $color-white;
                    }

                    >span:last-child {
                        color: $color-white;
                    }
                }

                .time {
                    background-color: $color-error-800;
                    border-color: $color-error-800;

                    span {
                        color: $color-white;
                    }

                    >span:last-child {
                        color: $color-white;
                    }
                }
            }

            .name {
                overflow: hidden;
                color: $color-black;
                text-overflow: ellipsis;
                font-family: $text-bold;
                @include font-size(28px);
                text-transform: uppercase;
                @include display-flex();
                @include align-items(center);
                max-height: 100px;
            }

            .units {
                @include display-flex();
                @include align-items(center);
                @include justify-content(center);
                @include flex-direction(column);
                padding: 17px 14px;
                border-radius: 4px;
                border: 2px solid $color-primary-500;
                background-color: $color-white;

                &.close-to-make {
                    background-color: $color-primary-100;
                }

                >span {
                    @include font-size(40px);
                    font-family: $text-bold;
                    color: $color-primary-500;
                    text-align: center;
                    line-height: 1;
                }

                >span:last-child {
                    @include font-size(18px);
                    font-family: $text;
                    color: $color-neutral-600;
                    text-align: center;
                }
            }

            .time {
                @include display-flex();
                @include align-items(center);
                @include justify-content(center);
                @include flex-direction(column);
                padding: 17px 14px;
                border-radius: 4px;
                border: 2px solid $color-neutral-200;
                background-color: $color-white;

                >span {
                    @include font-size(40px);
                    font-family: $text-bold;
                    color: $color-black;
                    text-align: center;
                    line-height: 1;
                }

                >span:last-child {
                    @include font-size(18px);
                    font-family: $text;
                    color: $color-neutral-600;
                    text-align: center;
                }
            }

            .i-andy-chevron-right {
                color: $color-primary-500;
                position: absolute;
                right: -32px;
                top: 50%;
                transform: translateY(-50%);
                cursor: pointer;
                padding: 8px;
            }
        }
    }

    @keyframes gradient {
        0% {
            background-position: 0% 0%;
            background-size: 200% 200%;
        }

        50% {
            background-position: 100% 100%;
            background-size: 200% 200%;
        }

        100% {
            background-position: 0% 0%;
            background-size: 200% 200%;
        }

    }

    /* PLS SIZES MONITORS*/
    &.landscape {
        .titles {
            grid-template-columns: 1.5fr 0.5fr 0.5fr;
            padding-right: 32px;
            span{
                @include font-size(18px);
                padding: 4px;
            }
        }
        .products {
            padding-right: 32px;
            .product {
                grid-template-columns: 1.5fr 0.5fr 0.5fr;

                .name{
                    @include font-size(24px);
                }
                .units{
                    min-width: 60px;
                    >span:first-child{
                        @include font-size(32px);
                    }
                    >span:last-child{
                        @include font-size(14px);
                    }
                }
                .time{
                    min-width: 60px;
                    >span:first-child{
                        @include font-size(32px);
                    }
                    >span:last-child{
                        @include font-size(14px);
                    }
                }
            }
        }
    }
}
</style>